import React, { useRef, useEffect, useMemo } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { designTokens } from 'utils/designSystem';
import { markdownToHtml, htmlToMarkdown } from 'utils/markdown';

/**
 * Rich Text Editor (Bold 지원)
 * - Ctrl+B: 굵게 토글
 * - 저장 시: HTML → 마크다운 변환
 * - 로드 시: 마크다운 → HTML 변환
 */
const RichTextEditor = ({
  value,
  onChange,
  placeholder = '내용을 입력하세요...',
  style = {},
  minHeight = '120px'
}) => {
  const quillRef = useRef(null);
  const lastExternalValue = useRef(value);

  // 외부 value가 변경되면 에디터 업데이트 (초기 로드 또는 외부에서 값 설정 시)
  useEffect(() => {
    if (quillRef.current && value !== lastExternalValue.current) {
      const editor = quillRef.current.getEditor();
      const currentHtml = editor.root.innerHTML;
      const currentMarkdown = htmlToMarkdown(currentHtml);

      // 실제로 값이 다를 때만 업데이트
      if (currentMarkdown !== value) {
        const html = markdownToHtml(value || '');
        editor.root.innerHTML = html || '';
      }
      lastExternalValue.current = value;
    }
  }, [value]);

  // Quill 모듈 설정
  const modules = useMemo(() => ({
    toolbar: false,
    keyboard: {
      bindings: {
        bold: {
          key: 'B',
          shortKey: true,
          handler: function(range, context) {
            this.quill.format('bold', !context.format.bold);
          }
        }
      }
    }
  }), []);

  const formats = ['bold'];

  // 값 변경 핸들러
  const handleChange = (content, delta, source, editor) => {
    // source 체크 제거 - Ctrl+B 등 포맷 변경 시 source가 'api'일 수 있음
    const markdown = htmlToMarkdown(content);

    // 값이 실제로 변경된 경우에만 업데이트 (무한루프 방지)
    if (markdown === lastExternalValue.current) {
      return;
    }

    lastExternalValue.current = markdown;
    onChange(markdown);
  };

  return (
    <div style={{ position: 'relative' }}>
      <style>{`
        .memo-editor .ql-container {
          border: 1px solid ${designTokens.colors.border};
          border-radius: ${designTokens.borderRadius.md};
          font-family: inherit;
          font-size: 14px;
          min-height: ${minHeight};
        }
        .memo-editor .ql-editor {
          min-height: ${minHeight};
          padding: ${designTokens.spacing.md};
          line-height: 1.6;
        }
        .memo-editor .ql-editor.ql-blank::before {
          color: ${designTokens.colors.text.muted};
          font-style: normal;
          left: ${designTokens.spacing.md};
        }
        .memo-editor .ql-container.ql-snow {
          border: 1px solid ${designTokens.colors.border};
          border-radius: ${designTokens.borderRadius.md};
        }
      `}</style>
      <div className="memo-editor" style={style}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          defaultValue={markdownToHtml(value || '')}
          onChange={handleChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
        />
      </div>
    </div>
  );
};

export default RichTextEditor;
